import { useCallback, useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { ProviderPermissionMatrixItem } from '../store/api';
import { useGetPermissionsMatrixQuery, useUpdatePermissionsMatrixMutation } from '../store/api';

interface PermissionsMatrixProps {
  title?: string;
  onClose?: () => void;
}

function buildPermissionsPayload(
  permissions: ProviderPermissionMatrixItem[],
  permissionId: string,
  roleId: string,
  enabled: boolean,
) {
  const payload: Record<string, Record<string, boolean>> = {};

  // Only configurable cells may appear in the PATCH — the backend rejects
  // overrides for restricted role cells (e.g. view_all_notes for supervisor).
  for (const permission of permissions) {
    payload[permission.permission] = {};
    for (const roleGrant of permission.roles ?? []) {
      if (roleGrant.configurable) {
        payload[permission.permission][roleGrant.role] = roleGrant.enabled;
      }
    }
  }

  payload[permissionId] = {
    ...(payload[permissionId] ?? {}),
    [roleId]: enabled,
  };

  return payload;
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === 'string') {
      return data.message;
    }
  }

  return 'Failed to update permissions';
}

/**
 * Editable org-override matrix (Practice Settings). Shows only the
 * org-configurable permissions and writes toggles through the permissions
 * PATCH endpoint. The read-only "what each role can do" reference table is a
 * separate component (RolePermissionsTable).
 */
export function PermissionsMatrix({ title = 'Permissions', onClose }: PermissionsMatrixProps = {}) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, refetch } = useGetPermissionsMatrixQuery();
  const [updatePermissions, { isLoading: isUpdating }] = useUpdatePermissionsMatrixMutation();

  const permissions = useMemo(() => data?.permissions ?? [], [data?.permissions]);

  // Only org-configurable permissions are editable; the endpoint returns the
  // full catalog, so filter to the configurable rows here.
  const configurablePermissions = useMemo(
    () => permissions.filter(p => p.configurable),
    [permissions],
  );
  const headerRoles = configurablePermissions[0]?.roles ?? [];

  const handleToggle = useCallback(
    async (permissionId: string, roleId: string, enabled: boolean) => {
      const nextPermissions = buildPermissionsPayload(
        configurablePermissions,
        permissionId,
        roleId,
        enabled,
      );

      try {
        await updatePermissions({
          providerUpdatePermissionsMatrixRequest: {
            permissions: nextPermissions,
          },
        }).unwrap();
        enqueueSnackbar('Permissions updated', { variant: 'success' });
        refetch();
      } catch (error: unknown) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    },
    [configurablePermissions, enqueueSnackbar, refetch, updatePermissions],
  );

  if (isLoading) {
    return (
      <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Loading permissions...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        avatar={<TuneIcon color="primary" />}
        title={title}
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheader="Set which roles can use each permission."
        action={
          onClose && (
            <IconButton aria-label="close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Permissions could not be loaded.
          </Alert>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              {headerRoles.map(role => (
                <col key={role.role} style={{ width: `${74 / (headerRoles.length || 1)}%` }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                {headerRoles.map(role => (
                  <TableCell key={role.role} align="center" sx={{ fontWeight: 600 }}>
                    {role.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {configurablePermissions.map(permission => (
                <TableRow key={permission.permission}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {permission.label}
                    </Typography>
                    {permission.description && (
                      <Typography variant="caption" color="text.secondary">
                        {permission.description}
                      </Typography>
                    )}
                  </TableCell>
                  {(permission.roles ?? []).map(role => (
                    <TableCell key={role.role} align="center">
                      {role.configurable ? (
                        <Switch
                          checked={role.enabled}
                          disabled={isUpdating}
                          onChange={event =>
                            handleToggle(permission.permission, role.role, event.target.checked)
                          }
                          slotProps={{
                            input: {
                              'aria-label': `Toggle ${permission.label} for ${role.label}`,
                            },
                          }}
                        />
                      ) : role.enabled ? (
                        <CheckIcon
                          fontSize="small"
                          color="success"
                          aria-label={`${permission.label} is always on for ${role.label}`}
                        />
                      ) : (
                        <RemoveIcon
                          fontSize="small"
                          color="disabled"
                          aria-label={`${permission.label} is not available for ${role.label}`}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {isUpdating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Updating permissions...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
